import {
  CancelledError,
  focusManager,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import type { CatalogPropertyPreview } from 'src/api/generated';
import {
  PROPERTY_PREVIEW_NULL_FRESHNESS_MS,
  propertyPreviewFreshUntil,
  propertyPreviewQueryKey,
  propertyPreviewQueryOptions,
} from 'src/modules/shared/utils/api/property-previews.util';
import { isApiAuthorizationError } from 'src/modules/shared/utils/api/query-error-policy.util';
import type {
  ConversationPropertyPreviewImageFailure,
  ConversationPropertyPreviewOptions,
  ConversationPropertyPreviewResult,
  ConversationPropertyPreviewVisibility,
} from '../types/property-preview.type';
import { usePropertyPreviewContext } from './provider-bounded/property-preview';
import { usePropertyPreviewVisibilityScope } from './provider-bounded/property-preview-visibility';

const UNAVAILABLE_ACCESS = { tenantId: '', userId: '', permissionKey: '' };
const cancelledLoader = (): Promise<CatalogPropertyPreview> =>
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- TanStack cancellation uses its public CancelledError class.
  Promise.reject(new CancelledError({ revert: true }));
const subscribeToFocus = (onChange: () => void): (() => void) =>
  focusManager.subscribe(onChange);
const isFocused = (): boolean => focusManager.isFocused();

/**
 * Observes one conversation's property through the view's shared batch coordinator.
 * Overlay mode reads the same QueryClient entry without requests or observers.
 * @param propertyId - This conversation's linked property, never the contact's property.
 * @param options - Cache-only mode for the drag overlay.
 * @returns Visibility ref, canonical authorized fields, display state and bounded image recovery.
 */
export const useConversationPropertyPreview = (
  propertyId: number | null | undefined,
  { cacheOnly = false }: ConversationPropertyPreviewOptions = {}
): ConversationPropertyPreviewResult => {
  const context = usePropertyPreviewContext();
  const scopeActive = usePropertyPreviewVisibilityScope();
  const queryClient = useQueryClient();
  const focused = useSyncExternalStore(subscribeToFocus, isFocused, isFocused);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [visibility, setVisibility] =
    useState<ConversationPropertyPreviewVisibility | null>(null);
  const [imageFailure, setImageFailure] =
    useState<ConversationPropertyPreviewImageFailure | null>(null);
  const validId =
    typeof propertyId === 'number' &&
    Number.isInteger(propertyId) &&
    propertyId > 0 &&
    propertyId <= 2147483647;
  const id = validId ? propertyId : 0;
  const access = context?.access ?? UNAVAILABLE_ACCESS;
  const accessKey = context?.accessKey ?? '';
  const eligible = validId && Boolean(context?.access);
  const visible =
    visibility !== null &&
    visibility.element === element &&
    visibility.accessKey === accessKey &&
    visibility.visible;
  const enabled = eligible && scopeActive && !cacheOnly && visible && focused;
  const queryKey = useMemo(
    () => propertyPreviewQueryKey(access, id),
    [access, id]
  );
  const cachedState =
    queryClient.getQueryState<CatalogPropertyPreview>(queryKey);
  const query = useQuery({
    ...propertyPreviewQueryOptions(
      access,
      id,
      context?.coordinator.load ?? cancelledLoader,
      cachedState?.data,
      cachedState?.dataUpdatedAt
    ),
    enabled,
  });

  const ref = useCallback(
    (node: HTMLElement | null): void => setElement(node),
    []
  );

  useEffect(() => {
    if (!element || !eligible || !scopeActive || cacheOnly || !context) return;
    return context.visibility.register(element, (isVisible) => {
      setVisibility({ element, accessKey, visible: isVisible });
    });
  }, [accessKey, cacheOnly, context, element, eligible, scopeActive]);

  useEffect(() => {
    if (enabled || cacheOnly || !eligible) return;
    // Wait for every observer's updated enabled option before cancelling shared work.
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      const entry = queryClient.getQueryCache().find({ queryKey, exact: true });
      if (entry && !entry.isActive()) {
        void queryClient.cancelQueries({ queryKey, exact: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cacheOnly, eligible, enabled, queryClient, queryKey]);

  const data = eligible ? query.data : undefined;
  const updatedAt = query.dataUpdatedAt;
  const { errorUpdatedAt, isError, isFetching, refetch } = query;

  useEffect(() => {
    if (!enabled || isFetching || (!data && !isError)) return;
    const freshUntil = data ? propertyPreviewFreshUntil(data, updatedAt) : 0;
    // Ordinary visible revalidation after a terminal failure is bounded
    // to one minute. All HTTP attempts still use the batch-owned retry policy.
    const refreshAt = isError
      ? errorUpdatedAt + PROPERTY_PREVIEW_NULL_FRESHNESS_MS
      : freshUntil;
    const timer = setTimeout(
      () => {
        void refetch({ cancelRefetch: false });
      },
      Math.max(0, refreshAt - Date.now())
    );
    return () => clearTimeout(timer);
  }, [data, enabled, errorUpdatedAt, isError, isFetching, refetch, updatedAt]);

  const usableImageUrl = data?.property?.imageUrl ?? null;
  const failedImage =
    imageFailure?.accessKey === accessKey &&
    imageFailure.propertyId === id &&
    imageFailure.updatedAt === updatedAt &&
    imageFailure.url === usableImageUrl;

  const onImageError = useCallback((): void => {
    if (!usableImageUrl || !eligible) return;
    setImageFailure({
      accessKey,
      propertyId: id,
      updatedAt,
      url: usableImageUrl,
    });
    if (enabled) context?.refreshImage(id, updatedAt);
  }, [accessKey, context, eligible, enabled, id, updatedAt, usableImageUrl]);

  const authorizationFailure = isApiAuthorizationError(query.error);
  const preview = authorizationFailure ? null : data?.property;
  const state =
    !eligible || authorizationFailure || preview === null
      ? 'unavailable'
      : preview
        ? 'ready'
        : query.isError
          ? 'error'
          : 'loading';

  return {
    ref,
    state,
    preview,
    imageUrl: failedImage || authorizationFailure ? null : usableImageUrl,
    onImageError,
  };
};
